import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTaskChatList,
  loadMoreTaskChatList,
  setSelectedTaskChatId,
  taskChatListMarkRead,
  TASK_CHAT_LIST_PAGE_SIZE,
} from "../../redux/Slice/Tasks/taskChatSlice";
import TaskChatCard from "./TaskChatCard";
import TaskChatPanel from "./TaskChatPanel";
import noConversationImage from "../../Images/Tasks/no-conversation-selected.svg";
import noTaskChatsImage from "../../Images/Tasks/no-task-chats.svg";
import ConnectionBanner from "../ConnectionBlocker/ConnectionBanner";
import {
  getConnectionStatus,
  retryConnection,
  onConnectionChange,
  onDisconnectionChange,
  onReconnectingChange,
  onReconnectFailedChange,
} from "../../utils/socketHandler";
import connectionMonitor from "../../utils/connectionMonitor";

function EmptyState() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 4,
        color: "text.secondary",
        borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box component="img" src={noConversationImage} alt="" sx={{ width: 240, height: 240, objectFit: "contain" }} />
      <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: "text.primary" }}>
        Select a task chat to start
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", textAlign: "center", maxWidth: 320 }}>
        Pick a task from the list on the left to view its conversation.
      </Typography>
    </Box>
  );
}

/** Auto-load more on scroll up to this many loaded chats; beyond that, a manual "More" button takes over (mirrors Inbox). */
const MAX_AUTO_SCROLL = 300;

function NoChatsState({ loading }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: 2, py: 4, gap: 1 }}>
      <Box component="img" src={noTaskChatsImage} alt="" sx={{ width: 180, height: 180, objectFit: "contain" }} />
      <Typography sx={{ fontWeight: 600, fontSize: "0.98rem", color: "text.primary" }}>
        No task chats found.
      </Typography>
    </Box>
  );
}

export default function TaskChatsTab({ onViewTask }) {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "unread"
  const [filterAnchor, setFilterAnchor] = useState(null);

  const chatList = useSelector((s) => s.taskChat?.chatList);
  const items = useMemo(() => chatList?.items || [], [chatList]);
  const loading = chatList?.loading || false;
  const loadingMore = chatList?.loadingMore || false;
  const selectedTaskId = chatList?.selectedTaskId || null;
  const chatListPagination = chatList?.pagination;
  const loadMorePendingRef = useRef(false);

  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: true,
    isReconnecting: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 2,
  });

  useEffect(() => {
    const tick = () => {
      try {
        const status = getConnectionStatus();
        if (status && typeof status === "object" && !status.message) {
          setConnectionStatus(status);
        }
      } catch (_) {
        setConnectionStatus({
          isConnected: false,
          isReconnecting: false,
          reconnectAttempts: 0,
          maxReconnectAttempts: 2,
        });
      }
    };

    const onOffline = () =>
      setConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        reconnectAttempts: 0,
        maxReconnectAttempts: 2,
      });

    tick();
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", tick);

    // Direct subscription to the connection monitor's real events, replacing
    // the previous 1s poll — this is what keeps isConnected/isReconnecting/the
    // attempt counter live and correct (the poll never reliably caught a
    // successful manual retryConnection(), leaving the banner stuck stale).
    const handleConnected = () => {
      setConnectionStatus((prev) => ({ ...prev, isConnected: true, isReconnecting: false, reconnectAttempts: 0 }));
    };
    const handleDisconnected = () => {
      setConnectionStatus((prev) => ({ ...prev, isConnected: false, isReconnecting: true }));
    };
    const handleReconnecting = (data) => {
      setConnectionStatus((prev) => ({
        ...prev,
        isConnected: false,
        isReconnecting: true,
        reconnectAttempts: data?.reconnectAttempts ?? prev.reconnectAttempts,
        maxReconnectAttempts: data?.maxReconnectAttempts ?? prev.maxReconnectAttempts,
      }));
    };
    const handleReconnectFailed = (data) => {
      const status = getConnectionStatus();
      setConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        reconnectAttempts: status.reconnectAttempts || (data?.reconnectAttempts ?? 0),
        maxReconnectAttempts: status.maxReconnectAttempts || (data?.maxReconnectAttempts ?? 2),
      });
    };

    onConnectionChange(handleConnected);
    onDisconnectionChange(handleDisconnected);
    onReconnectingChange(handleReconnecting);
    onReconnectFailedChange(handleReconnectFailed);

    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", tick);
      connectionMonitor.off('connection', handleConnected);
      connectionMonitor.off('disconnection', handleDisconnected);
      connectionMonitor.off('reconnecting', handleReconnecting);
      connectionMonitor.off('reconnectFailed', handleReconnectFailed);
    };
  }, []);

  const selectedItem = useMemo(
    () => items.find((i) => i.taskId === selectedTaskId) || null,
    [items, selectedTaskId]
  );

  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Load the chat list on mount, and re-fetch page 1 server-side whenever the
  // (debounced) search text changes.
  useEffect(() => {
    dispatch(fetchTaskChatList({ page: 1, limit: TASK_CHAT_LIST_PAGE_SIZE, search: debouncedSearch }));
  }, [dispatch, debouncedSearch]);

  // Clear selection on unmount
  useEffect(() => {
    return () => {
      dispatch(setSelectedTaskChatId(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!loadingMore) loadMorePendingRef.current = false;
  }, [loadingMore]);

  // Search is now server-side (see fetch effect above); only the "unread" chip
  // filters the already-loaded window client-side.
  const filteredItems = useMemo(
    () => (filter === "unread" ? items.filter((i) => i.unreadCount > 0) : items),
    [items, filter]
  );

  const handleScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
      if (!nearBottom) return;
      if (loadMorePendingRef.current || loadingMore) return;
      if (!chatListPagination?.hasMore) return;
      if (items.length >= MAX_AUTO_SCROLL) return; // manual "More" button takes over past this point
      loadMorePendingRef.current = true;
      dispatch(loadMoreTaskChatList());
    },
    [dispatch, loadingMore, chatListPagination?.hasMore, items.length]
  );

  const handleExplicitLoadMore = useCallback(() => {
    if (loadingMore || !chatListPagination?.hasMore) return;
    dispatch(loadMoreTaskChatList());
  }, [dispatch, loadingMore, chatListPagination?.hasMore]);

  const showLoadMoreButton = Boolean(chatListPagination?.hasMore) && items.length >= MAX_AUTO_SCROLL;

  const handleMarkAllRead = useCallback(() => {
    items.forEach((item) => {
      if (item.unreadCount > 0) {
        dispatch(taskChatListMarkRead({ taskId: item.taskId }));
      }
    });
    setFilterAnchor(null);
  }, [dispatch, items]);

  const handleSelectTask = useCallback(
    (item) => {
      const { taskId } = item;
      if (taskId === selectedTaskId) return;

      dispatch(setSelectedTaskChatId(taskId));
      // Optimistically clear the unread badge immediately; panel's own markTaskChatReadThunk
      // will clear it again (via fulfilled handler) once the server confirms.
      dispatch(taskChatListMarkRead({ taskId }));
      setShowChatOnMobile(true);
      // NOTE: fetchTaskChatMessages + markTaskChatReadThunk are handled by TaskChatPanel
      // on mount (key={selectedTaskId} forces remount on task change).
    },
    [dispatch, selectedTaskId]
  );

  const handleMobileBack = useCallback(() => {
    setShowChatOnMobile(false);
  }, []);

  const taskSummaryForPanel = selectedItem
    ? {
        title: selectedItem.taskName,
        type: selectedItem.taskType || undefined,
        status: selectedItem.taskStatus || undefined,
        priority: selectedItem.taskPriority || undefined,
        deadline: selectedItem.taskDeadline && dayjs(selectedItem.taskDeadline).isValid()
          ? dayjs(selectedItem.taskDeadline).format("DD/MM/YYYY h:mm A")
          : undefined,
        createdBy: selectedItem.taskCreatedBy || undefined,
        createdAt: selectedItem.taskCreatedAt && dayjs(selectedItem.taskCreatedAt).isValid()
          ? dayjs(selectedItem.taskCreatedAt).format("MMM D, YYYY h:mm A")
          : undefined,
        owners: selectedItem.taskOwners?.length ? selectedItem.taskOwners.join(", ") : undefined,
        assignees: selectedItem.taskAssignees?.length ? selectedItem.taskAssignees.join(", ") : undefined,
      }
    : null;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* ── Left panel: chat list ── */}
      <Box
        sx={{
          width: { xs: "100%", sm: "320px", md: "350px", lg: "380px" },
          flexShrink: 0,
          display: {
            xs: showChatOnMobile ? "none" : "flex",
            sm: "flex",
          },
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: (t) => t.palette.mode === "dark" && selectedTaskId ? "background.default" : undefined,
        }}
      >
        {/* Socket disconnect banner */}
        <ConnectionBanner
          connectionStatus={connectionStatus}
          onRetry={() => retryConnection()}
          sx={{ margin: 0, flexShrink: 0 }}
        />

        {/* Search bar + filter */}
        <Box sx={{ px: 1, py: 1.8, flexShrink: 0, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, display: "flex", alignItems: "center", gap: 0.75 }}>
          <IconButton
            size="small"
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            sx={{
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: "1.55rem" }} />
          </IconButton>
          <TextField
            size="small"
            fullWidth
            placeholder="Search task chats…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>

        {/* Filter menu */}
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { minWidth: 190, mt: 0.5, borderRadius: 1.5 } }}
        >
          <MenuItem
            onClick={() => { setFilter("all"); setFilterAnchor(null); }}
            sx={{ gap: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {filter === "all" && <CheckIcon sx={{ fontSize: "1rem", color: "primary.main" }} />}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.9rem" }}>All</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => { setFilter("unread"); setFilterAnchor(null); }}
            sx={{ gap: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {filter === "unread" && <CheckIcon sx={{ fontSize: "1rem", color: "primary.main" }} />}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.9rem" }}>Unread</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleMarkAllRead} sx={{ gap: 1 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <DoneAllIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.9rem" }}>Mark all as read</ListItemText>
          </MenuItem>
        </Menu>

        {/* Cards list */}
        <Box
          onScroll={handleScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: (t) =>
              t.palette.mode === "dark" ? "#555 #1a1a1a" : "#888 #f1f1f1",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (t) => t.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (t) => t.palette.mode === "dark" ? "#555" : "#888",
              borderRadius: "10px",
            },
          }}
        >
          {filteredItems.length === 0 ? (
            <NoChatsState loading={loading && items.length === 0} />
          ) : (
            <>
              {filteredItems.map((item) => (
                <TaskChatCard
                  key={item.taskId}
                  item={item}
                  selected={item.taskId === selectedTaskId}
                  onClick={() => handleSelectTask(item)}
                />
              ))}
              {loadingMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {/* "More" button — shown after the auto-scroll cap (300 chats) is reached */}
              {showLoadMoreButton && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExplicitLoadMore}
                    disabled={loadingMore}
                    sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "16px", px: 2.5 }}
                  >
                    {loadingMore ? "Loading…" : "More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ── Right panel: chat ── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: {
            xs: showChatOnMobile ? "flex" : "none",
            sm: "flex",
          },
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Mobile back button */}
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            alignItems: "center",
            px: 1,
            py: 0.5,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <IconButton size="small" onClick={handleMobileBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 600, ml: 0.5, color: "text.primary" }}>
            {selectedItem?.taskName || "Task Chat"}
          </Typography>
        </Box>

        {selectedTaskId ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <TaskChatPanel
              key={selectedTaskId}
              taskId={selectedTaskId}
              compact={true}
              taskSummary={taskSummaryForPanel}
              readOnly={selectedItem?.canSend === false}
              onViewTask={onViewTask || undefined}
            />
          </Box>
        ) : (
          <EmptyState />
        )}
      </Box>
    </Box>
  );
}
